import { createContext, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { RecordList } from "@/profitability/records/record-panel";
import type {
  ReportDataset,
  ReportRow,
  ReportRowDetail,
} from "@/profitability/types";

export interface OpenRowDetail {
  list?: RecordList;
  row: ReportRow;
  dataset: ReportDataset;
  detail?: ReportRowDetail;
  /**
   * The fields the report's own headline figures are built from, in the order
   * it shows them. The panel leads with these.
   */
  headline?: string[];
}

export interface RowDetailContext {
  open: OpenRowDetail | null;
  openRow: (value: OpenRowDetail) => void;
  closeRow: () => void;
}

const Context = createContext<RowDetailContext | null>(null);

/**
 * The row a report has open. It lives above the dashboard because the detail
 * shows in the page's one sidebar, the same slot the report chat uses, rather
 * than in a dialog over the page.
 */
export const RowDetailProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState<OpenRowDetail | null>(null);
  const value = useMemo(
    () => ({ open, openRow: setOpen, closeRow: () => setOpen(null) }),
    [open],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

/**
 * The same slot, kept by a page that decides itself where a row opens: the
 * template page sends a record to its record sheet and any other row to a
 * sheet of its own.
 */
export const RowDetailValueProvider = ({
  value,
  children,
}: {
  value: RowDetailContext;
  children: ReactNode;
}) => <Context.Provider value={value}>{children}</Context.Provider>;

/** Null outside a report page, so a table can be used on its own. */
export const useRowDetail = () => useContext(Context);
